import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Box, Paper, FormControl, InputLabel, Select, MenuItem, FormControlLabel, Checkbox } from '@mui/material';
import { fetchWithAuth } from '../services/api';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const getCurrentMonth = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${now.getFullYear()}-${month}`;
};

const buildRecentMonthOptions = (count = 24) => {
  const options = [];
  const cursor = new Date();
  cursor.setDate(1);
  for (let i = 0; i < count; i += 1) {
    const year = cursor.getFullYear();
    const month = String(cursor.getMonth() + 1).padStart(2, '0');
    const value = `${year}-${month}`;
    const label = cursor.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    options.push({ value, label });
    cursor.setMonth(cursor.getMonth() - 1);
  }
  return options;
};

function TenantRentCollection() {
  const [month, setMonth] = useState(getCurrentMonth());
  const [includeAverage, setIncludeAverage] = useState(true);
  const [tenantData, setTenantData] = useState(null);
  const [subsidyData, setSubsidyData] = useState(null);
  const [owners, setOwners] = useState([]);
  const [owner, setOwner] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const monthOptions = useMemo(() => buildRecentMonthOptions(24), []);
  const currentMonthIndex = useMemo(
    () => monthOptions.findIndex((option) => option.value === month),
    [monthOptions, month]
  );

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError('');

    const baseParams = new URLSearchParams({
      month,
      include_average: includeAverage ? 'true' : 'false',
    });

    if (owner) {
      baseParams.set('owner', owner);
    }

    const tenantParams = new URLSearchParams(baseParams);
    tenantParams.set('charge_type', 'Tenant Rent');

    const subsidyParams = new URLSearchParams(baseParams);
    subsidyParams.set('charge_type', 'Section 8 Rent');

    Promise.all([
      fetchWithAuth(`/api/data/analytics/tenant-rent-collection?${tenantParams.toString()}`)
        .then(response => response.json()),
      fetchWithAuth(`/api/data/analytics/tenant-rent-collection?${subsidyParams.toString()}`)
        .then(response => response.json())
    ])
      .then(([tenantResult, subsidyResult]) => {
        if (isMounted) {
          setTenantData(tenantResult);
        setOwners(tenantResult.owners || []);
          setSubsidyData(subsidyResult);
        }
      })
      .catch(err => {
        if (isMounted) {
          setError(err.message || 'Failed to load rent collection data.');
        }
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [month, includeAverage, owner]);

  const buildChartData = useCallback((data) => {
    if (!data) {
      return null;
    }

    const datasets = [
      {
        label: 'Cumulative Charged %',
        data: data.charged_percent || [],
        borderColor: 'rgb(153, 102, 255)',
        backgroundColor: 'rgba(153, 102, 255, 0.4)',
        fill: false,
        tension: 0.3,
        pointRadius: 2,
        pointHoverRadius: 4,
      },
      {
        label: 'Cumulative Paid %',
        data: data.paid_percent || [],
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.4)',
        fill: false,
        tension: 0.3,
        pointRadius: 2,
        pointHoverRadius: 4,
      }
    ];

    if (includeAverage && Array.isArray(data.average_paid_percent)) {
      datasets.push({
        label: '12-Month Avg Paid %',
        data: data.average_paid_percent,
        borderColor: 'rgb(255, 159, 64)',
        backgroundColor: 'rgba(255, 159, 64, 0.4)',
        borderDash: [6, 4],
        fill: false,
        tension: 0.2,
        pointRadius: 0,
        pointHoverRadius: 0,
      });
    }

    return {
      labels: data.days || [],
      datasets
    };
  }, [includeAverage]);

  const tenantChartData = useMemo(
    () => buildChartData(tenantData),
    [tenantData, buildChartData]
  );

  const subsidyChartData = useMemo(
    () => buildChartData(subsidyData),
    [subsidyData, buildChartData]
  );

  const buildChartOptions = (titleText) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        align: 'end',
        labels: {
          usePointStyle: true,
          padding: 20,
          font: { size: 12 },
          boxWidth: 10
        }
      },
      title: {
        display: true,
        text: titleText,
        font: {
          size: 16,
          weight: 'bold'
        },
        padding: {
          top: 10,
          bottom: 20
        }
      },
      tooltip: {
        enabled: true,
        mode: 'index',
        intersect: false,
        callbacks: {
          label: (context) => `${context.dataset.label}: ${context.parsed.y}%`
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        suggestedMax: 100,
        ticks: {
          callback: (value) => `${value}%`,
          font: { size: 12 },
          maxTicksLimit: 8
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        }
      },
      x: {
        ticks: {
          maxRotation: 0,
          minRotation: 0,
          font: { size: 12 },
          maxTicksLimit: 16
        },
        grid: {
          display: false
        },
        title: {
          display: true,
          text: 'Day of Month'
        }
      }
    }
  });

  const handlePreviousMonth = () => {
    if (currentMonthIndex === -1 || currentMonthIndex >= monthOptions.length - 1) {
      return;
    }
    setMonth(monthOptions[currentMonthIndex + 1].value);
  };

  const handleNextMonth = () => {
    if (currentMonthIndex <= 0) {
      return;
    }
    setMonth(monthOptions[currentMonthIndex - 1].value);
  };

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Paper elevation={2} sx={{ p: 3, width: '100%' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3, alignItems: 'center' }}>
          <FormControl sx={{ minWidth: 220 }}>
            <InputLabel id="rent-collection-month-label">Month</InputLabel>
            <Select
              labelId="rent-collection-month-label"
              value={month}
              label="Month"
              onChange={(event) => setMonth(event.target.value)}
            >
              {monthOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <button
              type="button"
              className="px-3 py-2 border rounded text-gray-700 disabled:opacity-50"
              onClick={handlePreviousMonth}
              disabled={currentMonthIndex === -1 || currentMonthIndex >= monthOptions.length - 1}
            >
              -1 Month
            </button>
            <button
              type="button"
              className="px-3 py-2 border rounded text-gray-700 disabled:opacity-50"
              onClick={handleNextMonth}
              disabled={currentMonthIndex <= 0}
            >
              +1 Month
            </button>
          </Box>
          <FormControl sx={{ minWidth: 220 }}>
            <InputLabel id="rent-collection-owner-label">Owner</InputLabel>
            <Select
              labelId="rent-collection-owner-label"
              value={owner}
              label="Owner"
              onChange={(event) => setOwner(event.target.value)}
            >
              <MenuItem value="">All owners</MenuItem>
              {owners.map((ownerName) => (
                <MenuItem key={ownerName} value={ownerName}>
                  {ownerName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={includeAverage}
                onChange={(event) => setIncludeAverage(event.target.checked)}
              />
            }
            label="Show 12-month average"
          />
          {tenantData?.total_charged !== undefined && (
            <span style={{ color: '#555' }}>
              Tenant charged: ${Number(tenantData.total_charged || 0).toLocaleString()}
            </span>
          )}
          {subsidyData?.total_charged !== undefined && (
            <span style={{ color: '#555' }}>
              Subsidy charged: ${Number(subsidyData.total_charged || 0).toLocaleString()}
            </span>
          )}
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '360px' }}>
            Loading...
          </Box>
        ) : error ? (
          <Box sx={{ color: 'red' }}>{error}</Box>
        ) : (
          <>
            <Box sx={{ height: '420px', width: '100%', position: 'relative', mb: 4 }}>
              {tenantChartData && (
                <Line
                  data={tenantChartData}
                  options={buildChartOptions('Tenant Rent Collection (Cumulative %)')}
                />
              )}
            </Box>
            <Box sx={{ height: '420px', width: '100%', position: 'relative' }}>
              {subsidyChartData && (
                <Line
                  data={subsidyChartData}
                  options={buildChartOptions('Subsidy Rent Collection (Cumulative %)')}
                />
              )}
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
}

export default TenantRentCollection;
